import React, { useRef } from "react"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import { ExportLicenses } from "./api"
import LicenseExportCompleteDialog from "./LicenseExportCompleteDialog"

/**
 * Reusable License Export Dialog Component
 * Handles the complete export flow with input dialog, API call, and success dialog
 */
export const LicenseExportDialog = {
  /**
   * Show export dialog and handle the complete export process
   * @param {Object} options - Export configuration
   * @param {string} [options.title="Export Licenses"] - Dialog title
   * @param {string} [options.defaultFilename="licenses"] - Default filename
   * @param {Object} [options.exportParams={}] - Additional parameters for ExportLicenses API
   * @param {Function} [options.onSuccess] - Callback when export succeeds
   * @param {Function} [options.onError] - Callback when export fails
   */
  show: ({
    title = "Export Licenses",
    defaultFilename = "licenses",
    exportParams = {},
    onSuccess,
    onError,
  }) => {
    const inputRef = React.createRef()

    showPromptDialog({
      title,
      message: (
        <div className="space-y-4">
          <Typography variant="admin_body" className="text-admin_text">
            Enter a filename for the exported licenses:
          </Typography>
          <Input
            ref={inputRef}
            variant="admin"
            placeholder={defaultFilename}
            defaultValue={defaultFilename}
            className="w-full"
            showClearButton
          />
        </div>
      ),
      confirmText: "Export",
      cancelText: "Close",
      onConfirm: async () => {
        const saveAsValue = inputRef.current?.value?.trim() || defaultFilename
        
        try {
          // Show loading notification
          showNotification({
            message: "Preparing export...",
            className: "bg-admin_primary text-admin_text_inv",
          })

          const response = await ExportLicenses({
            ...exportParams,
            saveAs: saveAsValue
          })

          if (response.success) {
            // Clear the "preparing" notification by showing a brief success notification
            showNotification({
              message: "Export ready!",
              className: "bg-admin_success text-white",
            })
            
            // Small delay to ensure notification shows, then show dialog
            setTimeout(() => {
              LicenseExportCompleteDialog.show(response)
            }, 100)
            
            onSuccess?.(response)
          } else {
            const errorMessage = response.message || response.error || "Failed to export licenses"
            showNotification({
              message: errorMessage,
              className: "bg-admin_error text-white",
            })
            onError?.(errorMessage)
          }
        } catch (error) {
          // Extract server error message from HTTP response
          let errorMessage = "An error occurred while exporting licenses"
          
          if (error.response?.data?.message) {
            // Server returned structured error with message
            errorMessage = error.response.data.message
          } else if (error.response?.data?.error) {
            // Server returned error code only
            errorMessage = error.response.data.error
          } else if (error.message) {
            // Network or other client error
            errorMessage = error.message
          }
          
          showNotification({
            message: errorMessage,
            className: "bg-admin_error text-white",
          })
          onError?.(errorMessage)
        }
      },
    })
  },

  /**
   * Quick export for main licenses page
   * @param {Object} requestFields - Current DataTable request fields (search, match, etc.)
   */
  exportAllLicenses: (requestFields = {}) => {
    LicenseExportDialog.show({
      title: "Export Licenses",
      defaultFilename: "licenses",
      exportParams: requestFields,
    })
  },

  /**
   * Quick export for pack-specific licenses
   * @param {string} packId - Pack ID to filter licenses
   * @param {string} packName - Pack name for default filename
   * @param {Object} requestFields - Current DataTable request fields (search, match, etc.)
   */
  exportPackLicenses: (packId, packName, requestFields = {}) => {
    const defaultFilename = `${packName || 'pack'}-licenses`.replace(/[^a-zA-Z0-9-_]/g, '-')
    
    LicenseExportDialog.show({
      title: "Export Pack Licenses",
      defaultFilename,
      exportParams: {
        packId,
        ...requestFields,
      },
    })
  },
}

export default LicenseExportDialog